import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  createContext,
  ReactNode,
} from "react";

import local_storage from "../../utils/local_storage";

export const DEFAULT_PER_PAGE = 10;

/** What `Login` decodes out of the JWT. `false` until someone signs in. */
export type User = {
  id: string;
  email: string;
  role: string;
};

export type GlobalState = {
  isMenuOpened: boolean;
  isHeaderButtonActionDisabled: boolean;
  user: User | false;
  currencySymbol: string;
  tablePerPage: number;
};

export type GlobalContextValue = GlobalState & {
  setGlobalContext: (patch: Partial<GlobalState>) => void;
};

// Whatever was persisted wins over the defaults, so a reload keeps the collapsed menu and the
// page size. `local_storage.get` is untyped by design — it hands back whatever JSON parsed.
const initialState: GlobalState = {
  isMenuOpened: true,
  isHeaderButtonActionDisabled: false,
  user: false,
  currencySymbol: "$",
  tablePerPage: DEFAULT_PER_PAGE,
  ...(local_storage.get("globalContext") || {}),
};

// Same as DataContext: the default is only reached with no provider mounted, and it used to be
// the bare state, missing the `setGlobalContext` the provider supplies — so every
// `const { setGlobalContext } = useGlobalContext()` was unchecked.
const missingProvider = (member: string) => () => {
  throw new Error(`${member} used outside GlobalContextProvider`);
};

const GlobalContext = createContext<GlobalContextValue>({
  ...initialState,
  setGlobalContext: missingProvider("setGlobalContext"),
});

export const useGlobalContext = () => useContext(GlobalContext);

const GlobalContextProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState(initialState);

  const setGlobalContext = useCallback(
    (patch: Partial<GlobalState>) => setState(prev => ({ ...prev, ...patch })),
    []
  );

  useEffect(() => {
    local_storage.set("globalContext", state);
  }, [state]);

  // Memoised: the spread built a new object on every render of this provider, so every consumer
  // re-rendered even when the state it reads had not moved.
  const value = useMemo(() => ({ ...state, setGlobalContext }), [state, setGlobalContext]);

  return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
};

export default GlobalContextProvider;
